import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, 
  MapPin, 
  Clock, 
  Star, 
  CheckCircle, 
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  X,
  Heart,
  Shield,
  Award,
  Users,
  Eye,
  Radio,
  Sparkles,
  Scissors,
  Stethoscope,
  Plane
} from 'lucide-react';
import { hospitalInfo, services, specialOffers } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import { useSettings } from '../contexts/SettingsContext';
import axios from 'axios';
import PopupDisplay from '../components/PopupDisplay';
import { CMSComponentRenderer } from '../components/cms';

const Home = () => {
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [heroImages, setHeroImages] = useState([]);
  const { urgent_care_enabled } = useSettings();
  const [heroImagesLoading, setHeroImagesLoading] = useState(true);
  const [activeTemplate, setActiveTemplate] = useState('default');
  const [pageComponents, setPageComponents] = useState([]);
  
  // Debug logging for urgent care settings
  console.log('=== HOME PAGE DEBUG ===');
  console.log('urgent_care_enabled received in Home component:', urgent_care_enabled);
  console.log('Type:', typeof urgent_care_enabled);
  console.log('=== END HOME PAGE DEBUG ===');
  const [hospitalHours, setHospitalHours] = useState(null);
  const [urgentCareHours, setUrgentCareHours] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(true);
  const [businessTimezone, setBusinessTimezone] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(true);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Fetch home page CMS components
  useEffect(() => {
    const fetchHomePageComponents = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/public/cms/pages/`);
        if (response.data.success && response.data.page && response.data.page.components) {
          // Fetch component details
          const componentsResponse = await axios.get(`${API_BASE_URL}/api/public/cms/components`);
          const allComponents = componentsResponse.data.components || [];
          
          // Map component IDs to full component data
          const resolvedComponents = response.data.page.components.map(componentId => {
            return allComponents.find(c => c.id === componentId);
          }).filter(c => c); // Filter out any not found
          
          setPageComponents(resolvedComponents);
        }
      } catch (error) {
        console.error('Error fetching home page components:', error);
      }
    };
    fetchHomePageComponents();
  }, []);

  // Fetch active template for home page
  useEffect(() => {
    const fetchActiveTemplate = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/page-templates/active/home`);
        if (response.data && response.data.template) {
          setActiveTemplate(response.data.template.layout || 'default');
        }
      } catch (err) {
        console.error('Error fetching home template:', err);
        setActiveTemplate('default');
      }
    };
    fetchActiveTemplate();
  }, []);

  // Fetch slider images from API
  useEffect(() => {
    const fetchSliderImages = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/slider-images`);
        if (response.data && response.data.slider_images) {
          // Sort by order field (ascending) to display in proper order
          const sortedImages = response.data.slider_images.sort((a, b) => (a.order || 0) - (b.order || 0));
          setHeroImages(sortedImages);
        }
        setHeroImagesLoading(false);
      } catch (err) {
        console.error('Error fetching slider images:', err);
        // Fallback to static images if API fails
        setHeroImages([
          {
            id: 1,
            title: "Professional Veterinary Care",
            description: "Compassionate care for your beloved pets",
            image_url: "https://customer-assets.emergentagent.com/job_peacock-pet-care/artifacts/ulwulpak_emilee.png",
            order: 0
          },
          {
            id: 2,
            title: "State-of-the-Art Facility",
            description: "Modern equipment and comfortable environment",
            image_url: "https://customer-assets.emergentagent.com/job_peacock-pet-care/artifacts/ej59vv47_vanama.png",
            order: 1
          }
        ]);
        setHeroImagesLoading(false);
      }
    };

    fetchSliderImages();
  }, []);

  // Auto-rotate hero images
  React.useEffect(() => {
    if (heroImages.length > 0) {
      const interval = setInterval(() => {
        setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [heroImages]);

  // Fetch hours and business info from database
  useEffect(() => {
    const fetchHours = async () => {
      try {
        // Fetch hospital hours
        const hospitalResponse = await fetch(`${API_BASE_URL}/api/hospital-hours`);
        if (hospitalResponse.ok) {
          const hospitalData = await hospitalResponse.json();
          setHospitalHours(hospitalData);
        }

        // Fetch urgent care hours
        const urgentResponse = await fetch(`${API_BASE_URL}/api/urgent-care-hours`);
        if (urgentResponse.ok) {
          const urgentData = await urgentResponse.json();
          setUrgentCareHours(urgentData);
        }

        // Fetch business info for timezone
        const businessResponse = await fetch(`${API_BASE_URL}/api/business-info`);
        if (businessResponse.ok) {
          const businessData = await businessResponse.json();
          if (businessData.timezone) {
            setBusinessTimezone(businessData.timezone);
          }
        }
      } catch (error) {
        console.error('Error fetching hours:', error);
      } finally {
        setHoursLoading(false);
      }
    };

    fetchHours();
  }, []);

  // Fetch reviews from database
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // First, check if dynamic reviews are enabled
        const settingsResponse = await fetch(`${API_BASE_URL}/api/reviews/check-dynamic`);
        const settingsData = await settingsResponse.json();
        
        let reviewsToDisplay = [];
        
        if (settingsData.dynamic_reviews_enabled) {
          // Fetch Google reviews
          console.log('Fetching Google reviews...');
          const googleResponse = await fetch(`${API_BASE_URL}/api/reviews/google`);
          if (googleResponse.ok) {
            const googleData = await googleResponse.json();
            
            // Check for errors from the API
            if (googleData.error) {
              console.error('Google Reviews API Error:', googleData.error);
              // Show error to user - fallback to empty for now
              reviewsToDisplay = [];
            } else {
              reviewsToDisplay = googleData.reviews || [];
              // Filter to show only 4+ star reviews
              reviewsToDisplay = reviewsToDisplay.filter(review => review.rating >= 4);
              console.log('Google reviews fetched:', reviewsToDisplay);
            }
          } else {
            console.error('Failed to fetch Google reviews - HTTP error');
          }
        } else {
          // Fetch manual reviews
          console.log('Fetching manual reviews...');
          const response = await fetch(`${API_BASE_URL}/api/reviews`);
          if (response.ok) {
            const data = await response.json();
            reviewsToDisplay = data.reviews || [];
            console.log('Manual reviews fetched:', reviewsToDisplay);
          }
        }
        
        setTestimonials(reviewsToDisplay);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        // Fallback to empty array if API fails
        setTestimonials([]);
      } finally {
        setTestimonialsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Helper function to truncate review text
  const truncateText = (text, maxWords = 23) => {
    if (!text) return text;
    const words = text.split(' ');
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  // Handle review modal
  const openReviewModal = (review) => {
    setSelectedReview(review);
    setShowReviewModal(true);
  };

  const closeReviewModal = () => {
    setShowReviewModal(false);
    setSelectedReview(null);
  };

  // Smooth continuous scrolling - one review at a time
  const nextReview = () => {
    if (isTransitioning) return; // Prevent multiple clicks during transition
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % testimonials.length);
      setIsTransitioning(false);
    }, 50); // Small delay for smooth transition
  };

  const prevReview = () => {
    if (isTransitioning) return; // Prevent multiple clicks during transition
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentReviewIndex((prev) => 
        prev === 0 ? testimonials.length - 1 : prev - 1
      );
      setIsTransitioning(false);
    }, 50); // Small delay for smooth transition
  };

  // Auto scroll reviews every 3 seconds (smooth continuous rotation)
  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        nextReview();
      }, 3000); // Scroll every 3 seconds for smooth continuous feel
      return () => clearInterval(interval);
    }
  }, [testimonials.length, currentReviewIndex]);

  // Helper function to get current date/time in business timezone
  const getCurrentBusinessDate = () => {
    let currentDate = new Date();
    if (businessTimezone) {
      try {
        const tzDateString = currentDate.toLocaleString('en-US', { timeZone: businessTimezone });
        currentDate = new Date(tzDateString);
      } catch (error) {
        console.error('Error converting to business timezone:', error);
      }
    }
    return currentDate;
  };

  // Helper function to get current day of week
  const getCurrentDay = () => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDate = getCurrentBusinessDate();
    return days[currentDate.getDay()];
  };

  // Helper function to get next open day
  const getNextOpenDay = (hoursData, startFromDay = null) => {
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    const startIndex = startFromDay ? days.indexOf(startFromDay) : new Date().getDay();
    
    for (let i = 1; i <= 7; i++) {
      const dayIndex = (startIndex + i) % 7;
      const dayKey = days[dayIndex];
      if (hoursData[dayKey]?.is_open) {
        const dayName = dayNames[dayIndex];
        const openTime = formatTime(hoursData[dayKey].open_time);
        return { day: dayName, time: openTime, isToday: i === 0, isTomorrow: i === 1 };
      }
    }
    return null;
  };

  // Helper function to format time from 24-hour to 12-hour
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to check if currently open
  const isCurrentlyOpen = (dayData) => {
    if (!dayData?.is_open || !dayData.open_time || !dayData.close_time) return false;
    
    const now = getCurrentBusinessDate();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight
    
    const [openHour, openMin] = dayData.open_time.split(':').map(Number);
    const [closeHour, closeMin] = dayData.close_time.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime < closeTime;
  };

  // Helper function to check if opening soon (within 2 hours)
  const isOpeningSoon = (dayData) => {
    if (!dayData?.is_open || !dayData.open_time) return false;
    
    const now = getCurrentBusinessDate();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [openHour, openMin] = dayData.open_time.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    
    const timeDiff = openTime - currentTime;
    return timeDiff > 0 && timeDiff <= 120; // opening within 2 hours
  };

  // Helper function to check if closing soon (within 1 hour)
  const isClosingSoon = (dayData) => {
    if (!dayData?.is_open || !dayData.close_time) return false;
    
    const now = getCurrentBusinessDate();
    const currentTime = now.getHours() * 60 + now.getMinutes();
    
    const [closeHour, closeMin] = dayData.close_time.split(':').map(Number);
    const closeTime = closeHour * 60 + closeMin;
    
    const timeDiff = closeTime - currentTime;
    return timeDiff > 0 && timeDiff <= 60; // closing within 1 hour
  };

  // Generate status message for hours display
  const getHoursStatus = (hoursData, type) => {
    if (!hoursData || hoursLoading) {
      return type === 'urgent' ? '3 PM - 10 PM Daily' : '9:00 AM - 6:00 PM Today';
    }

    const currentDay = getCurrentDay();
    const todayHours = hoursData[currentDay];

    if (!todayHours?.is_open) {
      const nextOpen = getNextOpenDay(hoursData);
      if (nextOpen) {
        if (nextOpen.isTomorrow) {
          return `Closed today • Opens tomorrow at ${nextOpen.time}`;
        } else {
          return `Closed today • Opens ${nextOpen.day} at ${nextOpen.time}`;
        }
      }
      return 'Closed today';
    }

    if (isCurrentlyOpen(todayHours)) {
      // Check if closing soon while open
      if (isClosingSoon(todayHours)) {
        return `Closing soon at ${formatTime(todayHours.close_time)}`;
      }
      return `Open now • Closes at ${formatTime(todayHours.close_time)}`;
    }

    if (isOpeningSoon(todayHours)) {
      return `Opening soon at ${formatTime(todayHours.open_time)}`;
    }

    return `${formatTime(todayHours.open_time)} - ${formatTime(todayHours.close_time)} Today`;
  };

  const primaryColor = 'var(--theme-primary)';
  const primaryLight = 'var(--theme-primary-light)';
  const primaryBg = 'var(--theme-primary-light)';

  return (
    <div className="min-h-screen">
      {/* Popup Display */}
      <PopupDisplay pageUrl="/" />
      
      {/* Hero Section - Conditional based on active template */}
      {activeTemplate === 'full_width_hero' ? (
        <>
        {/* Full Width Hero Layout */}
        <section className="relative w-full" style={{ height: '500px' }}>
            {/* Hero Image Container - Full width */}
            <div className="absolute inset-0">
              {heroImagesLoading ? (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: primaryColor }}></div>
                </div>
              ) : heroImages.length > 0 ? (
                <>
                  {heroImages.map((image, index) => (
                    <div
                      key={image.id}
                      className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                        index === currentHeroImage ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      <img
                        src={image.image_url}
                        alt={image.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60"></div>
                  
                  {/* Content Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-full px-6 sm:px-12 text-center text-white">
                      <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-white/20 backdrop-blur-sm mb-4">
                        <Heart className="h-4 w-4 mr-2" />
                        Where Every Paw Finds Care
                      </div>
                      <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 leading-tight">
                        Compassionate Care for Your
                        <span className="block" style={{ color: 'var(--theme-secondary)' }}>Beloved Pets</span>
                      </h1>
                      <p className="text-sm md:text-base opacity-90 mb-5 max-w-xl mx-auto">
                        {urgent_care_enabled ? (
                          <>Quality veterinary care and urgent care services for dogs and cats.</>
                        ) : (
                          <>Quality veterinary care and specialty care services for dogs and cats.</>
                        )}
                      </p>
                      
                      {/* CTA Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        {urgent_care_enabled && (
                          <Link
                            to="/urgent-care-booking"
                            className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 bg-white hover:bg-gray-100 text-sm"
                            style={{ color: primaryColor }}
                          >
                            Check In Online
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Link>
                        )}
                        <a
                          href={`tel:${currentBusinessInfo.phone}`}
                          className="inline-flex items-center justify-center text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 shadow-lg text-sm"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {currentBusinessInfo.phone}
                          <Phone className="ml-2 h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                  
                  {/* Slider Navigation Dots */}
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                    {heroImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentHeroImage(index)}
                        className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          index === currentHeroImage ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
                        }`}
                      />
                    ))}
                  </div>
                  
                  {/* Navigation Arrows */}
                  <button
                    onClick={() => setCurrentHeroImage((prev) => (prev - 1 + heroImages.length) % heroImages.length)}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentHeroImage((prev) => (prev + 1) % heroImages.length)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/30 hover:bg-black/50 text-white transition-all"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <h3 className="text-xl font-bold mb-2">Professional Veterinary Care</h3>
                  </div>
                </div>
              )}
            </div>
        </section>
            
        {/* Quick Info Bar - Constrained width */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
            <div className="bg-white shadow-lg rounded-xl p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <Clock className="h-5 w-5" style={{ color: primaryColor }} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Urgent Care</p>
                    <p className="text-xs text-gray-600">
                      {hoursLoading ? 'Loading...' : getHoursStatus(urgentCareHours, 'urgent')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 justify-center">
                  <MapPin className="h-5 w-5" style={{ color: primaryColor }} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">General Practice</p>
                    <p className="text-xs text-gray-600">
                      {hoursLoading ? 'Loading...' : getHoursStatus(hospitalHours, 'general')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-end">
                  <Phone className="h-5 w-5" style={{ color: primaryColor }} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">Call Us</p>
                    <p className="text-xs text-gray-600">{currentBusinessInfo.phone}</p>
                  </div>
                </div>
              </div>
            </div>
        </div>
        </>
      ) : (
        /* Default Layout - Split Hero */
        <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${primaryBg} 0%, #ffffff 50%, #f0fdff 100%)` }}>
          <div className="absolute inset-0 bg-white/40"></div>
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" style={{ paddingBottom: '20px' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              {/* Left Content */}
              <div className="space-y-4">
                <div className="space-y-4">
                  <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: primaryBg, color: primaryColor }}>
                    <Heart className="h-4 w-4 mr-2" />
                    Where Every Paw Finds Care
                  </div>
                  <h1 className="font-bold text-gray-900 leading-tight" style={{ fontSize: '2rem' }}>
                    Compassionate Care for Your
                    <span className="block" style={{ color: primaryColor }}>Beloved Pets</span>
                  </h1>
                  <p className="text-gray-600 leading-relaxed" style={{ fontSize: '1rem' }}>
                    {urgent_care_enabled ? (
                      <>
                        Quality veterinary care and urgent care services for dogs and cats. 
                        Modern facilities, experienced veterinarians, and extended hours to serve all pet owners throughout Northern Virginia.
                      </>
                    ) : (
                      <>
                        Quality veterinary care and specialty care services for dogs and cats. 
                        Modern facilities and experienced veterinarians to serve all pet owners throughout Northern Virginia.
                      </>
                    )}
                  </p>
                </div>

                {/* Info and CTA Compartments */}
                <div className={`grid grid-cols-1 ${urgent_care_enabled ? 'sm:grid-cols-2' : ''} gap-6`}>
                  {/* Left Compartment - Urgent Care */}
                  {urgent_care_enabled && (
                    <div className="flex flex-col h-full">
                      <div className="flex items-start space-x-3 bg-white/60 backdrop-blur-sm py-4 pr-4 pl-0 rounded-lg h-20 mb-4 flex-shrink-0">
                        <Clock className="h-5 w-5 mt-1" style={{ color: primaryColor }} />
                        <div className="text-left flex-1">
                          <p className="font-semibold text-gray-900">Urgent Care</p>
                          <p className="text-sm text-gray-600 leading-tight">
                            {hoursLoading ? (
                              <span className="animate-pulse">Loading hours...</span>
                            ) : (
                              getHoursStatus(urgentCareHours, 'urgent')
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex-1 flex items-end">
                        <Link
                          to="/urgent-care-booking"
                          className="inline-flex items-center justify-center border-2 px-8 py-4 rounded-lg font-semibold transition-all duration-200 w-full"
                          style={{ 
                            borderColor: primaryColor, 
                            color: primaryColor,
                            height: '56px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = primaryColor;
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = primaryColor;
                          }}
                        >
                          Check In Online
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Right Compartment - General Practice */}
                  <div className="flex flex-col h-full">
                    <div className="flex items-start space-x-3 bg-white/60 backdrop-blur-sm py-4 pr-4 pl-0 rounded-lg h-20 mb-4 flex-shrink-0">
                      <MapPin className="h-5 w-5 mt-1" style={{ color: primaryColor }} />
                      <div className="text-left flex-1">
                        <p className="font-semibold text-gray-900">General Practice</p>
                        <p className="text-sm text-gray-600 leading-tight">
                          {hoursLoading ? (
                            <span className="animate-pulse">Loading hours...</span>
                          ) : (
                            getHoursStatus(hospitalHours, 'general')
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex-1 flex items-end">
                      <a
                        href={`tel:${currentBusinessInfo.phone}`}
                        className="inline-flex items-center justify-center text-white px-8 py-4 rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl group w-full"
                        style={{ backgroundColor: primaryColor, height: '56px' }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
                      >
                        {currentBusinessInfo.phone}
                        <Phone className="ml-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                      </a>
                    </div>
                  </div>
                </div>
            </div>

            {/* Right Content - Hero Image */}
            <div className="relative">
              {heroImagesLoading ? (
                <div className="relative rounded-lg overflow-hidden shadow-2xl bg-gray-100 flex items-center justify-center" style={{ height: '25rem' }}>
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
              ) : heroImages.length > 0 ? (
                <>
                  <div className="relative rounded-lg overflow-hidden shadow-2xl">
                    {/* Image Stack for Smooth Transitions */}
                    <div className="relative w-full" style={{ height: '25rem' }}>
                      {heroImages.map((image, index) => (
                        <div
                          key={image.id}
                          className={`absolute inset-0 transition-all duration-1000 ease-in-out transform ${
                            index === currentHeroImage 
                              ? 'opacity-100 scale-100 translate-x-0' 
                              : 'opacity-0 scale-105 translate-x-4'
                          }`}
                        >
                          <img
                            src={image.image_url}
                            alt={image.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                          <div className="hidden w-full h-full items-center justify-center bg-gray-100 absolute inset-0">
                            <div className="text-center text-gray-500">
                              <Users className="h-16 w-16 mx-auto mb-2" />
                              <p>Image not available</p>
                            </div>
                          </div>
                          
                          {/* Bottom gradient overlay - 30% height for text area with darker gradient */}
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" style={{ height: '30%' }}></div>
                          
                          {/* Image title and description overlay */}
                          <div className={`absolute bottom-6 left-6 right-6 text-white transition-all duration-700 delay-300 ${
                            index === currentHeroImage 
                              ? 'opacity-100 translate-y-0' 
                              : 'opacity-0 translate-y-4'
                          }`}>
                            <h3 className="text-xl font-bold mb-2">{image.title}</h3>
                            <p className="text-sm opacity-90">{image.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                  </div>
                  

                </>
              ) : (
                <div className="relative rounded-lg overflow-hidden shadow-2xl bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center" style={{ height: '25rem' }}>
                  <div className="text-center text-gray-600">
                    <Users className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-xl font-bold mb-2">Professional Veterinary Care</h3>
                    <p className="text-sm opacity-75">Compassionate care for your beloved pets</p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>
      )}

      {/* Urgent Care / Specialty Care Section */}
      <section className="p-6 md:p-8" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-6xl mx-auto">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center">
                <div className="h-px bg-white/30 flex-grow max-w-16"></div>
                <div className="mx-3 px-5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                  <h2 className="font-bold text-white text-base tracking-wide">
                    {urgent_care_enabled ? 'Urgent Care' : 'Specialty Care'}
                  </h2>
                </div>
                <div className="h-px bg-white/30 flex-grow max-w-16"></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Poison or Toxin Exposure</h3>
                <p className="text-gray-600 mb-4">
                  Rapid treatment including safe induced vomiting and detox protocols
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Upper Respiratory Infections</h3>
                <p className="text-gray-600 mb-4">
                  Our team will diagnose the issue and recommend the best treatment to help your pet breathe easier
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Urinary Concerns</h3>
                <p className="text-gray-600 mb-4">
                  Evaluation and care for straining, blood, or frequent attempts
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Diarrhea & Vomiting</h3>
                <p className="text-gray-600 mb-4">
                  We quickly diagnose and treat pet GI issues—bringing relief to your pet
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Coughing & Sneezing</h3>
                <p className="text-gray-600 mb-4">
                  Diagnostics and treatment plans tailored to breed and symptoms
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: 'var(--theme-primary-light)' }}>
                  <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Allergic Reactions</h3>
                <p className="text-gray-600 mb-4">
                  Treatment for hives, facial swelling, and itching
                </p>
              </div>
            </div>

            {/* All Services Button */}
            <div className="text-center mt-6">
              <Link
                to={urgent_care_enabled ? "/urgent-care" : "/specialty-services"}
                className="inline-flex items-center justify-center px-6 py-2.5 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg text-sm"
              >
                View All Services
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
        </div>
      </section>

      {/* General Practice Pet Care */}
      <section className="bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-4">
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
              <div className="mx-4 px-6 py-2 bg-white backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
                <h2 className="font-bold text-gray-900 text-lg tracking-wide">
                  General Practice Pet Care
                </h2>
              </div>
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
            </div>
            <p className="text-gray-600 max-w-3xl mx-auto" style={{ fontSize: '1rem' }}>
              Comprehensive preventive and routine veterinary services to keep your pet healthy throughout their life.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Vaccinations */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#10b98115' }}>
                <Shield className="h-8 w-8" style={{ color: '#10b981' }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Vaccinations</h3>
              <p className="text-gray-600 mb-4">Essential immunizations to protect your pet from serious diseases like parvovirus, distemper, and rabies with tailored vaccination schedules.</p>
              <Link
                to="/pet-vaccinations"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: '#10b981' }}
                onMouseEnter={(e) => e.target.style.color = '#059669'}
                onMouseLeave={(e) => e.target.style.color = '#10b981'}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Wellness Exams */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: primaryBg }}>
                <Stethoscope className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Wellness Exams</h3>
              <p className="text-gray-600 mb-4">Comprehensive physical assessments including eye exams, heart screening, and full body evaluations to detect health issues early.</p>
              <Link
                to="/preventive-pet-care"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: primaryColor }}
                onMouseEnter={(e) => e.target.style.color = 'var(--theme-primary-hover)'}
                onMouseLeave={(e) => e.target.style.color = primaryColor}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Microchipping */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#3b82f615' }}>
                <Radio className="h-8 w-8" style={{ color: '#3b82f6' }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Microchipping</h3>
              <p className="text-gray-600 mb-4">Permanent identification system using a tiny electronic chip to help reunite you with your lost pet quickly and safely.</p>
              <Link
                to="/pet-microchipping"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: '#3b82f6' }}
                onMouseEnter={(e) => e.target.style.color = '#2563eb'}
                onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Dental Cleaning */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#f59e0b15' }}>
                <Sparkles className="h-8 w-8" style={{ color: '#f59e0b' }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Dental Cleaning</h3>
              <p className="text-gray-600 mb-4">Professional dental care including plaque removal, dental X-rays, and comprehensive oral examinations for optimal dental health.</p>
              <Link
                to="/dental-cleanings"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: '#f59e0b' }}
                onMouseEnter={(e) => e.target.style.color = '#d97706'}
                onMouseLeave={(e) => e.target.style.color = '#f59e0b'}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Travel Certificates */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#8b5cf615' }}>
                <Plane className="h-8 w-8" style={{ color: '#8b5cf6' }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Travel Certificates</h3>
              <p className="text-gray-600 mb-4">Domestic Health Certificates for safe pet travel by air, road, or sea. Federally accredited veterinarian certification within 10 days of departure.</p>
              <Link
                to="/pet-travel-certificates"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: '#8b5cf6' }}
                onMouseEnter={(e) => e.target.style.color = '#7c3aed'}
                onMouseLeave={(e) => e.target.style.color = '#8b5cf6'}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Spay & Neuter */}
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-1">
              <div className="flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#8b5cf615' }}>
                <Scissors className="h-8 w-8" style={{ color: '#8b5cf6' }} />
              </div>
              <h3 className="font-bold text-gray-900 mb-3" style={{ fontSize: '1rem' }}>Spay & Neuter</h3>
              <p className="text-gray-600 mb-4">Safe surgical procedures to prevent diseases, reduce behavioral issues, and support responsible pet ownership with health and community benefits.</p>
              <Link
                to="/pet-spay-neuter"
                className="inline-flex items-center font-semibold transition-colors"
                style={{ color: '#8b5cf6' }}
                onMouseEnter={(e) => e.target.style.color = '#7c3aed'}
                onMouseLeave={(e) => e.target.style.color = '#8b5cf6'}
              >
                Learn More
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>

          {/* View All Button */}
          <div className="text-center mt-8">
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-3 font-semibold rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
              style={{ 
                backgroundColor: primaryColor,
                color: 'white'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-primary-hover)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = primaryColor}
            >
              View All Services
              <ChevronRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white" style={{ paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center" style={{ marginBottom: '20px' }}>
            <div className="inline-flex items-center justify-center mb-4">
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
              <div className="mx-4 px-6 py-2 bg-white backdrop-blur-sm rounded-full border border-gray-200 shadow-sm">
                <h2 className="font-bold text-gray-900 text-lg tracking-wide">
                  What Pet Parents Say
                </h2>
              </div>
              <div className="h-px bg-gray-300 flex-grow max-w-24"></div>
            </div>
            <p className="text-gray-600" style={{ fontSize: '1rem' }}>
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>

          {testimonialsLoading ? (
            // Loading state
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-50 p-6 rounded-xl animate-pulse">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, j) => (
                      <div key={j} className="h-5 w-5 bg-gray-300 rounded mr-1"></div>
                    ))}
                  </div>
                  <div className="h-4 bg-gray-300 rounded mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded mb-4"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : testimonials.length > 0 ? (
            // Reviews carousel
            <div className="relative">
              {/* Navigation arrows */}
              {testimonials.length > 3 && (
                <>
                  <button
                    onClick={prevReview}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                    style={{ color: primaryColor }}
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    onClick={nextReview}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                    style={{ color: primaryColor }}
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}

              {/* Reviews grid - Show 3 reviews at a time with wrapping */}
              <div className="overflow-hidden">
                <div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-500 ease-in-out"
                  key={currentReviewIndex}
                >
                  {(() => {
                    // Create array of 3 reviews starting from currentReviewIndex with wrapping
                    const displayReviews = [];
                    for (let i = 0; i < Math.min(3, testimonials.length); i++) {
                      const index = (currentReviewIndex + i) % testimonials.length;
                      displayReviews.push(testimonials[index]);
                    }
                    return displayReviews;
                  })().map((testimonial, idx) => (
                    <div
                      key={testimonial.id}
                      className="bg-gray-50 p-6 rounded-xl hover:shadow-lg review-card-transition"
                      style={{
                        animation: 'fadeInSlide 0.6s ease-out',
                        animationDelay: `${idx * 0.1}s`,
                        animationFillMode: 'both'
                      }}
                    >
                    {/* Star rating */}
                    <div className="flex items-center mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`h-5 w-5 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                        />
                      ))}
                      {testimonial.rating && (
                        <span className="ml-2 text-sm text-gray-600 font-medium">
                          {testimonial.rating}/5
                        </span>
                      )}
                    </div>

                    {/* Review text */}
                    <p className="text-gray-700 mb-4 italic">
                      "{truncateText(testimonial.text, 23)}"
                    </p>

                    {/* Read more button */}
                    {testimonial.text && testimonial.text.split(' ').length > 23 && (
                      <button
                        onClick={() => openReviewModal(testimonial)}
                        className="text-sm font-medium mb-4 hover:underline"
                        style={{ color: primaryColor }}
                      >
                        Read More →
                      </button>
                    )}

                    {/* Author info */}
                    <div className="mt-auto">
                      <p className="font-semibold text-gray-900">{testimonial.owner_name}</p>
                      {testimonial.pet_name && (
                        <p className="text-sm text-gray-600">Pet parent to {testimonial.pet_name}</p>
                      )}
                      {testimonial.relative_time && (
                        <p className="text-xs text-gray-500 mt-1">{testimonial.relative_time}</p>
                      )}
                    </div>
                  </div>
                ))}
                </div>
              </div>

              {/* Pagination dots - one per review */}
              {testimonials.length > 1 && (
                <div className="flex justify-center mt-6 space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentReviewIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        currentReviewIndex === index 
                          ? 'w-8' 
                          : 'w-2'
                      }`}
                      style={{ 
                        backgroundColor: currentReviewIndex === index ? primaryColor : '#d1d5db'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            // No testimonials available
            <div className="col-span-full text-center py-8">
              <p className="text-gray-500">No customer reviews available yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* Review Modal */}
      {showReviewModal && selectedReview && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={closeReviewModal}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Customer Review</h3>
              <button
                onClick={closeReviewModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal content */}
            <div className="p-6">
              {/* Star rating */}
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`h-6 w-6 ${i < selectedReview.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} 
                  />
                ))}
                <span className="ml-2 text-gray-600 font-medium">
                  {selectedReview.rating}/5
                </span>
              </div>

              {/* Full review text */}
              <p className="text-gray-700 mb-6 text-base leading-relaxed italic">
                "{selectedReview.text}"
              </p>

              {/* Author info */}
              <div className="border-t border-gray-200 pt-4">
                <p className="font-semibold text-gray-900 text-lg">{selectedReview.owner_name}</p>
                {selectedReview.pet_name && (
                  <p className="text-gray-600">Pet parent to {selectedReview.pet_name}</p>
                )}
                {selectedReview.relative_time && (
                  <p className="text-sm text-gray-500 mt-2">{selectedReview.relative_time}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Render CMS Page Components */}
      {pageComponents.length > 0 && pageComponents.map((component, index) => (
        <CMSComponentRenderer
          key={`${component.id}-${index}`}
          componentType={component.type}
          config={component.config}
        />
      ))}

      {/* Contact CTA */}
      <section style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)`, paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-white mb-4" style={{ fontSize: '1rem' }}>
            Ready to Give Your Pet the Best Care?
          </h2>
          <p className="mb-8" style={{ color: 'white', fontSize: '1rem' }}>
            Contact us today to schedule an appointment or for urgent care
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${currentBusinessInfo.phone}`}
              className="inline-flex items-center justify-center bg-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
              style={{ color: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now: {currentBusinessInfo.phone}
            </a>
            <Link
              to="/reach-us"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';  
                e.target.style.color = 'white';
              }}
            >
              Get Directions
              <MapPin className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;